import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Reveal, Kicker, WfButton, ImageWithCredit } from "@/components/site/primitives";
import { ProgramHero } from "@/components/site/programs/ProgramHero";
import { PhaseImmersive } from "@/components/site/PhaseImmersive";
import { PROGRAMS_INTRO, PROGRAMS_OVERVIEW } from "@/content/programs";

const ProgramOverview = ({ program, index }) => {
  const flip = index % 2 === 1;
  return (
    <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <Reveal className={flip ? "order-2 lg:order-2" : "order-2 lg:order-1"} delay={0.05}>
        <ImageWithCredit
          src={program.image}
          alt={program.imageAlt}
          className="aspect-[5/4] w-full rounded-sm shadow-[var(--wf-shadow)]"
          imgClassName="object-cover"
        />
      </Reveal>
      <Reveal className={flip ? "order-1 lg:order-1" : "order-1 lg:order-2"}>
        <Kicker>{program.eyebrow}</Kicker>
        <h2 className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
          {program.title}
        </h2>
        <p className="mt-4 max-w-[54ch] text-base leading-7 text-[var(--wf-soil)]">{program.body}</p>
        <WfButton
          as={Link}
          to={program.cta.href}
          withArrow
          className="mt-8 px-7"
          data-testid={`programs-cta-${program.id}`}
        >
          {program.cta.label}
        </WfButton>
      </Reveal>
    </div>
  );
};

export default function ProgramsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <ProgramHero
          eyebrow={PROGRAMS_INTRO.eyebrow}
          title={PROGRAMS_INTRO.title}
          lede={PROGRAMS_INTRO.lede}
          image={PROGRAMS_INTRO.image}
          imageAlt={PROGRAMS_INTRO.imageAlt}
        />

        <section id="programs-list" aria-label="Our programs" className="wf-section bg-[var(--wf-cream)]">
          <div className="wf-container space-y-16 sm:space-y-20 lg:space-y-24">
            {PROGRAMS_OVERVIEW.map((p, i) => (
              <ProgramOverview key={p.id} program={p} index={i} />
            ))}
          </div>
        </section>

        <PhaseImmersive />

        {/* Closing band */}
        <section aria-labelledby="programs-outro-heading" className="bg-white py-16 sm:py-20 lg:py-24">
          <div className="wf-container">
            <div className="relative overflow-hidden rounded-sm bg-[var(--wf-maroon)] px-6 py-14 text-center sm:px-10 sm:py-16">
              <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--wf-clay)]/30 blur-2xl" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-[var(--wf-wheat)]/20 blur-2xl" />
              <div className="relative mx-auto max-w-2xl">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">Join the community</span>
                <h2 id="programs-outro-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">Grow with World Farmers</h2>
                <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.85)]">Whether you want to farm, support a farmer, or learn more about our story, there\u2019s a place for you here.</p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <WfButton as="a" href="/support" variant="light" withArrow className="px-7" data-testid="programs-outro-support-button">Support a farmer</WfButton>
                  <WfButton as="a" href="/who-we-are" className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="programs-outro-who-button">Meet our team</WfButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
